/**
 * list_projects Tool - Discover projects within an organization
 *
 * Projects contain test jobs and templates. Use this to find the right project
 * before creating jobs.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';

export interface ListProjectsArgs {
  organizationId?: string;
}

export const listProjectsToolDefinition: Tool = {
  name: 'list_projects',
  description: `List projects accessible to the authenticated user.

Projects contain test jobs and templates. Each project belongs to an organization.

Optionally filter by organization ID to see only that org's projects.
If no organizationId is provided, returns all projects the user can access.

Typical workflow:
1. list_organizations → Find the right organization
2. list_projects(organizationId: "...") → Find the right project
3. create_job(projectId: "...") → Create a test job`,
  inputSchema: {
    type: 'object',
    properties: {
      organizationId: {
        type: 'string',
        description: 'Filter projects by organization ID. If omitted, returns all accessible projects.',
      },
    },
    required: [],
  },
};

export async function handleListProjects(
  args: ListProjectsArgs,
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const data = await jobClient.listProjects(args.organizationId);

    if (data.items.length === 0) {
      const scopeMsg = args.organizationId
        ? `No projects found in this organization.`
        : `No projects found.`;

      return {
        content: [{
          type: 'text',
          text: `${scopeMsg}

Create a project in the dashboard: https://runhuman.com/dashboard`,
        }],
      };
    }

    const projectList = data.items.map(p => {
      let entry = `- ${p.name} (ID: ${p.id})`;
      if (p.githubRepos?.length) entry += `\n  GitHub: ${p.githubRepos.map(r => r.repo).join(', ')}`;
      if (p.defaultUrl) entry += `\n  Default URL: ${p.defaultUrl}`;
      return entry;
    }).join('\n\n');

    const scopeMsg = args.organizationId
      ? `Found ${data.items.length} project(s) in organization:`
      : `Found ${data.items.length} project(s):`;

    return {
      content: [{
        type: 'text',
        text: `${scopeMsg}

${projectList}

Next steps:
- Create a job: create_job(projectId: "<project-id>")
- List templates: list_templates(projectId: "<project-id>")`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };

    return {
      content: [{
        type: 'text',
        text: `Failed to list projects

Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
